/**
 * DataExplorer — AG Grid Enterprise wrapper with Integrated Charts.
 *
 * This is the "JMP equivalent": engineers see their run table data,
 * right-click to chart any columns, and cross-filter between views.
 */

import { useMemo, useEffect, useCallback, useState, useRef } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, CellSelectionOptions, AdvancedFilterModel, FilterChangedEvent, GridReadyEvent, RowClickedEvent, RowStyle, SelectionChangedEvent, RowSelectionOptions, GetMainMenuItemsParams, GetContextMenuItemsParams, MenuItemDef } from "ag-grid-community";
import { themeQuartz, colorSchemeDark } from "ag-grid-community";

import { useTheme } from "../../context/ThemeContext";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { usePlotData } from "../../context/PlotDataContext";
import { useViewState, type ViewStateSlice } from "../../context/ViewStateContext";
import { fetchRunTablePaginated } from "../../api/runTable";
import type { RunTableData } from "../../api/runTable";
import { useBranches, useMoveNodes } from "../../hooks/useVersioning";
import { Correlogram, type CorrelogramColumn } from "./Correlogram";
import { ColumnSelector, AUTO_GROUP_KEY } from "./ColumnSelector";
import { filterConstantColumns } from "./correlationRanking";
import { buildColumnDefs, detectSmartGroupColumns, detectBestColorBy, isCategory, type DisplayPrecision, type ColumnPrecisionMap } from "./columnBuilder";
import { ALL_CUSTOM_AGGS } from "./aggFunctions";
import { detectSmartGroups, groupByField, removeOutliers, type DataGroup, type SmartGroupOverrides, type SmartGroupExplanation } from "./groupDetection";
import { SmartGroupPanel } from "./SmartGroupPanel";
import { useDataPointContextMenu, DataPointContextMenu } from "../shared/DataPointContextMenu";

type ActiveTab = "table" | "correlogram";

const HIDDEN_FIELDS = new Set(["_group", "_path_prefix", "_node_id", "_geometry_node_id", "_is_stale", "_stale_reason", "_resolved_from"]);
const DEFAULT_HIDDEN_SUFFIX = "_f360";

/** Stable empty array to avoid new reference on every render when data is undefined. */
const EMPTY_ROWS: Record<string, unknown>[] = [];

/** Row count thresholds for progressive performance optimization. */
const PERF_TIER_MEDIUM = 1000;
const PERF_TIER_LARGE = 3000;

/** Pagination constants for progressive loading.
 *
 * The backend's `/run-table?limit=&offset=` currently builds the full table
 * server-side then slices it in memory, so two paged requests do ~2× the
 * work of a single unpaged fetch. Until server-side pagination is real,
 * we only progressive-load for very large projects where a fast first
 * render actually saves wall-clock time over waiting for the full set.
 */
const INITIAL_PAGE_SIZE = 1000;
const PROGRESSIVE_THRESHOLD = 3000;
const FULL_LOAD_THRESHOLD = 5000;

interface Props {
  projectId: string;
  isVisible?: boolean;
}

const btnStyle: React.CSSProperties = {
  padding: "2px 8px", borderRadius: 4, border: "1px solid var(--gray-a6)",
  fontSize: 12, cursor: "pointer",
};

const IN_FLIGHT_STATUSES = ["pending", "running", "queued", "submitted", "preprocessing", "uploading"];

export function DataExplorer({ projectId, isVisible = true }: Props) {
  const { theme } = useTheme();
  const gridTheme = useMemo(() => theme === "dark" ? themeQuartz.withPart(colorSchemeDark) : themeQuartz, [theme]);
  const { currentBranch, selectedArtifactId, selectArtifact, selectArtifactAndOpenPanel } = useProvenanceContext();
  const { setFullData, setFilteredData } = usePlotData();
  const { data: branches } = useBranches(projectId);
  const moveNodesMutation = useMoveNodes(projectId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasInFlight, setHasInFlight] = useState(false);
  const [checkedNodeIds, setCheckedNodeIds] = useState<string[]>([]);
  const [moveBranchOpen, setMoveBranchOpen] = useState(false);
  const [showUncorrected, setShowUncorrected] = useState(() => searchParams.get("uncorrected") === "1");
  const [precision, setPrecision] = useState<DisplayPrecision>(() => {
    const p = searchParams.get("precision");
    return p != null ? Number(p) : null;
  });
  const [columnPrecision, setColumnPrecision] = useState<ColumnPrecisionMap>(() => {
    try {
      const encoded = searchParams.get("colPrec");
      return encoded ? JSON.parse(atob(encoded)) as ColumnPrecisionMap : {};
    } catch { return {}; }
  });
  const [smartGroupActive, setSmartGroupActive] = useState(() => searchParams.get("smartGroup") === "1");
  const [smartGroupOverrides, setSmartGroupOverrides] = useState<SmartGroupOverrides>(() => {
    try {
      const encoded = searchParams.get("sgOverrides");
      return encoded ? JSON.parse(atob(encoded)) as SmartGroupOverrides : {};
    } catch { return {}; }
  });
  const [smartGroupExplanation, setSmartGroupExplanation] = useState<SmartGroupExplanation | null>(null);
  const [smartGroupPanelCollapsed, setSmartGroupPanelCollapsed] = useState(false);

  const toggleUncorrected = useCallback(() => {
    setShowUncorrected((prev) => {
      const next = !prev;
      setSearchParams((sp) => {
        const p = new URLSearchParams(sp);
        if (next) p.set("uncorrected", "1");
        else p.delete("uncorrected");
        return p;
      }, { replace: true });
      return next;
    });
  }, [setSearchParams]);

  const activeTab: ActiveTab = (searchParams.get("tab") === "correlogram") ? "correlogram" : "table";
  const setActiveTab = useCallback((tab: ActiveTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === "table") next.delete("tab");
      else next.set("tab", tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [selectedColumnKeys, setSelectedColumnKeys] = useState<Set<string>>(new Set());
  const [colorByKey, setColorByKey] = useState<string | undefined>();
  const [groupByKey, setGroupByKey] = useState<string | undefined>(() => searchParams.get("groupBy") || undefined);
  const [sizeByKey, setSizeByKey] = useState<string | undefined>();
  const [symbolByKey, setSymbolByKey] = useState<string | undefined>();
  const [zAxisKey, setZAxisKey] = useState<string | undefined>();
  const [excludeOutliers, setExcludeOutliers] = useState(false);
  const [dataSource, setDataSource] = useState<"all" | "filtered" | "aggregated">("all");
  const [correlogramRows, setCorrelogramRows] = useState<Record<string, unknown>[]>([]);
  const [filteredRows, setFilteredRows] = useState<Record<string, unknown>[]>([]);
  const [aggregatedRows] = useState<Record<string, unknown>[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const filterRestoredRef = useRef(false);

  // -- Register Data Explorer state for sharing --
  const { register } = useViewState();
  const selectedColumnKeysRef = useRef(selectedColumnKeys);
  selectedColumnKeysRef.current = selectedColumnKeys;
  const colorByKeyRef = useRef(colorByKey);
  colorByKeyRef.current = colorByKey;

  useEffect(() => register(
    "dataExplorer",
    () => {
      const api = gridRef.current?.api;
      return {
        selectedColumns: [...selectedColumnKeysRef.current],
        colorByKey: colorByKeyRef.current ?? null,
        showUncorrected,
        filterModel: api?.getAdvancedFilterModel() ?? null,
        columnState: api?.getColumnState() ?? [],
        sortModel: api?.getColumnState()
          ?.filter((c: { sort?: string | null }) => c.sort)
          .map((c: { colId: string; sort?: string | null; sortIndex?: number | null }) => ({
            colId: c.colId, sort: c.sort, sortIndex: c.sortIndex,
          })) ?? [],
      };
    },
    (state: ViewStateSlice) => {
      if (state.selectedColumns) setSelectedColumnKeys(new Set(state.selectedColumns as string[]));
      if (state.colorByKey !== undefined) setColorByKey((state.colorByKey as string) || undefined);
      if (state.showUncorrected !== undefined) setShowUncorrected(state.showUncorrected as boolean);
      const api = gridRef.current?.api;
      if (api && state.filterModel) api.setAdvancedFilterModel(state.filterModel as AdvancedFilterModel);
      if (api && state.columnState) api.applyColumnState({ state: state.columnState as never[], applyOrder: true });
    },
  ), [register, showUncorrected]);

  // -- Probe: fetch a tiny first page so we know the project size. The
  //    server sends `total` with any paginated response, so this is a
  //    sub-second round-trip that lets us decide whether to do a full
  //    single fetch (cheaper: one backend build) or a progressive load. --
  const { data: probe, isLoading: isLoadingProbe } = useQuery({
    queryKey: ["run-table", projectId, currentBranch, "probe"],
    queryFn: () => fetchRunTablePaginated({ projectId, branch: currentBranch, limit: 1, offset: 0 }),
    refetchInterval: isVisible && hasInFlight ? 15_000 : false,
  });
  const totalRows = probe?.total ?? 0;

  // Small/medium project: single unpaged fetch (backend builds the table once).
  const wantSingleFetch = !!probe && totalRows <= PROGRESSIVE_THRESHOLD;
  const { data: singleFetch, isLoading: isLoadingSingle } = useQuery({
    queryKey: ["run-table", projectId, currentBranch, "full"],
    queryFn: () => fetchRunTablePaginated({ projectId, branch: currentBranch }),
    enabled: wantSingleFetch,
    placeholderData: keepPreviousData,
    refetchInterval: isVisible && hasInFlight ? 15_000 : false,
  });

  // Large project: progressive load. Accept the 2× backend cost as the
  // trade-off for faster first-paint.
  const wantProgressive = !!probe && totalRows > PROGRESSIVE_THRESHOLD;
  const { data: page1, isLoading: isLoadingPage1 } = useQuery({
    queryKey: ["run-table", projectId, currentBranch, "page1"],
    queryFn: () => fetchRunTablePaginated({ projectId, branch: currentBranch, limit: INITIAL_PAGE_SIZE, offset: 0 }),
    enabled: wantProgressive,
    placeholderData: keepPreviousData,
    refetchInterval: isVisible && hasInFlight ? 15_000 : false,
  });

  const restLimit = Math.min(
    Math.max(totalRows - INITIAL_PAGE_SIZE, 0),
    FULL_LOAD_THRESHOLD - INITIAL_PAGE_SIZE,
  );
  const hasMore = wantProgressive && totalRows > INITIAL_PAGE_SIZE;

  const { data: page2, isLoading: isLoadingRest } = useQuery({
    queryKey: ["run-table", projectId, currentBranch, "rest", restLimit],
    queryFn: () => fetchRunTablePaginated({
      projectId, branch: currentBranch,
      limit: restLimit,
      offset: INITIAL_PAGE_SIZE,
    }),
    enabled: hasMore,
    placeholderData: keepPreviousData,
    refetchInterval: isVisible && hasInFlight ? 15_000 : false,
  });

  const data: RunTableData | undefined = useMemo(() => {
    if (singleFetch) {
      return { columns: singleFetch.columns, rows: singleFetch.rows, total: totalRows };
    }
    if (!page1) return undefined;
    const allRows = page2?.rows ? [...page1.rows, ...page2.rows] : page1.rows;
    return { columns: page1.columns, rows: allRows, total: totalRows };
  }, [singleFetch, page1, page2, totalRows]);

  const isLoading = isLoadingProbe || (wantSingleFetch && isLoadingSingle) || (wantProgressive && isLoadingPage1);

  const applySmartGroup = useCallback((overridesArg?: SmartGroupOverrides) => {
    const api = gridRef.current?.api;
    if (!api || !data?.rows || !data?.columns) return;
    const ov = overridesArg ?? smartGroupOverrides;
    const groupCols = new Set(detectSmartGroupColumns(data.rows, data.columns, ov));
    if (groupCols.size === 0) return;
    const colState = api.getColumns()?.map((col) => {
      const id = col.getColId();
      return {
        colId: id,
        rowGroup: groupCols.has(id),
        hide: id === "__alpha_stall" || id === "__alpha_ldmax" ? false : undefined,
      };
    }) ?? [];
    api.applyColumnState({ state: colState });
    setSmartGroupActive(true);
    setSearchParams((sp) => { const p = new URLSearchParams(sp); p.set("smartGroup", "1"); return p; }, { replace: true });
  }, [data, setSearchParams, smartGroupOverrides]);

  const clearGroups = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.applyColumnState({
      defaultState: { rowGroup: false },
      state: [
        { colId: "__alpha_stall", hide: true },
        { colId: "__alpha_ldmax", hide: true },
      ],
    });
    setSmartGroupActive(false);
    setSmartGroupOverrides({});
    setSmartGroupExplanation(null);
    setSearchParams((sp) => {
      const p = new URLSearchParams(sp);
      p.delete("smartGroup");
      p.delete("sgOverrides");
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const numericColumnDefs = useMemo(() => {
    if (!data?.columns) return [];
    return data.columns.filter(col =>
      !isCategory(col.field) &&
      !HIDDEN_FIELDS.has(col.field) &&
      (showUncorrected || !col.field.endsWith(DEFAULT_HIDDEN_SUFFIX)),
    );
  }, [data?.columns, showUncorrected]);

  const nonConstantNumericDefs = useMemo(() => {
    if (!data?.rows || numericColumnDefs.length === 0) return numericColumnDefs;
    return filterConstantColumns(data.rows, numericColumnDefs);
  }, [numericColumnDefs, data?.rows]);

  const allColumnOptions = useMemo(() => {
    if (!data?.columns) return [];
    return data.columns.filter(col => !HIDDEN_FIELDS.has(col.field));
  }, [data?.columns]);

  const didInitColumns = useRef(false);
  useEffect(() => {
    if (didInitColumns.current || nonConstantNumericDefs.length === 0) return;
    didInitColumns.current = true;
    setSelectedColumnKeys(new Set(nonConstantNumericDefs.slice(0, 10).map(c => c.field)));

    if (!colorByKey && !groupByKey && data?.rows && data.rows.length > 0 && data.columns) {
      const runDetect = () => {
        const best = detectBestColorBy(data.rows, data.columns);
        if (best) {
          setGroupByKey(AUTO_GROUP_KEY);
          setColorByKey(best);
        }
      };
      if ("requestIdleCallback" in window) {
        (window as Window & typeof globalThis & { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(runDetect);
      } else {
        setTimeout(runDetect, 0);
      }
    }
  }, [nonConstantNumericDefs, colorByKey, groupByKey, data]);

  const prevRowFingerprintRef = useRef("");
  useEffect(() => {
    if (!data?.rows) return;
    // Only propagate to context if rows actually changed (avoids cascading
    // re-renders through PlotDataContext → Correlogram → PlotBuilder on
    // every 15-second refetch that returns identical data). Cheap signature
    // — length + first/last ids + sampled ids — avoids joining 2000+ uuids
    // into one long string on every poll. False positives (unchanged rows
    // reported as changed) are harmless; false negatives (changed rows
    // reported as unchanged) would skip an update so we sample enough
    // positions to catch realistic run-level mutations.
    const rows = data.rows;
    const first = (rows[0]?._node_id ?? "") as string;
    const last = (rows[rows.length - 1]?._node_id ?? "") as string;
    const mid = rows.length > 2 ? ((rows[rows.length >> 1]?._node_id ?? "") as string) : "";
    const fp = `${rows.length}|${first}|${mid}|${last}`;
    if (fp !== prevRowFingerprintRef.current) {
      prevRowFingerprintRef.current = fp;
      setFullData(rows);
      setFilteredRows(rows);
    }
    setHasInFlight(
      rows.some((r) => IN_FLIGHT_STATUSES.includes(r.status as string)),
    );
  }, [data?.rows, setFullData]);

  const effectiveRows = useMemo(() => {
    const base = dataSource === "aggregated" ? aggregatedRows
      : dataSource === "filtered" ? filteredRows
      : (data?.rows ?? EMPTY_ROWS);
    return excludeOutliers && numericColumnDefs.length > 0
      ? removeOutliers(base, numericColumnDefs.map((c) => c.field))
      : base;
  }, [dataSource, data?.rows, filteredRows, aggregatedRows, excludeOutliers, numericColumnDefs]);

  useEffect(() => {
    setCorrelogramRows(effectiveRows);
  }, [effectiveRows]);

  const correlogramGroups = useMemo<DataGroup[] | undefined>(() => {
    if (!groupByKey || !data?.columns) return undefined;
    if (groupByKey === AUTO_GROUP_KEY) {
      const plotted = [...selectedColumnKeys];
      const result = detectSmartGroups(effectiveRows, data.columns, plotted, smartGroupOverrides);
      setSmartGroupExplanation(result.explanation);
      return result.groups;
    }
    setSmartGroupExplanation(null);
    return groupByField(effectiveRows, groupByKey);
  }, [groupByKey, effectiveRows, data?.columns, selectedColumnKeys, smartGroupOverrides]);

  // Generate explanation whenever smart group is active (for the panel),
  // even on the table tab where correlogramGroups may not trigger.
  useEffect(() => {
    if (!smartGroupActive || !data?.rows || !data?.columns) {
      if (!smartGroupActive) setSmartGroupExplanation(null);
      return;
    }
    const result = detectSmartGroups(data.rows, data.columns, [], smartGroupOverrides);
    setSmartGroupExplanation(result.explanation);
  }, [smartGroupActive, data?.rows, data?.columns, smartGroupOverrides]);

  const allFieldNames = useMemo(() => {
    if (!data?.columns) return [];
    return data.columns.map((c) => c.field);
  }, [data?.columns]);

  const handleSmartGroupOverridesChange = useCallback((ov: SmartGroupOverrides) => {
    setSmartGroupOverrides(ov);
    setSearchParams((sp) => {
      const p = new URLSearchParams(sp);
      if ((ov.include?.length ?? 0) > 0 || (ov.exclude?.length ?? 0) > 0) {
        p.set("sgOverrides", btoa(JSON.stringify(ov)));
      } else {
        p.delete("sgOverrides");
      }
      return p;
    }, { replace: true });
    // Re-apply to AG Grid table grouping too
    applySmartGroup(ov);
  }, [setSearchParams, applySmartGroup]);

  const activeCorrelogramColumns = useMemo<CorrelogramColumn[]>(() =>
    nonConstantNumericDefs
      .filter(c => selectedColumnKeys.has(c.field))
      .map(c => ({ key: c.field, header: c.headerName ?? c.field })),
  [nonConstantNumericDefs, selectedColumnKeys]);

  

  const handleFilterChanged = useCallback((event: FilterChangedEvent) => {
    const rowData: Record<string, unknown>[] = [];
    event.api.forEachNodeAfterFilter((node) => {
      if (node.data) rowData.push(node.data as Record<string, unknown>);
    });
    setFilteredData(rowData);
    setFilteredRows(rowData);
    const model = event.api.getAdvancedFilterModel();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!model) next.delete("filters");
      else next.set("filters", btoa(JSON.stringify(model)));
      return next;
    }, { replace: true });
  }, [setFilteredData, setSearchParams]);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    if (filterRestoredRef.current) return;
    const encoded = searchParams.get("filters");
    if (encoded) {
      try {
        const model = JSON.parse(atob(encoded)) as AdvancedFilterModel;
        event.api.setAdvancedFilterModel(model);
        filterRestoredRef.current = true;
      } catch { /* ignore malformed filters */ }
    }
    if (smartGroupActive && data?.rows && data?.columns) {
      const groupCols = new Set(detectSmartGroupColumns(data.rows, data.columns, smartGroupOverrides));
      if (groupCols.size > 0) {
        const colState = event.api.getColumns()?.map((col) => {
          const id = col.getColId();
          return {
            colId: id,
            rowGroup: groupCols.has(id),
            hide: id === "__alpha_stall" || id === "__alpha_ldmax" ? false : undefined,
          };
        }) ?? [];
        event.api.applyColumnState({ state: colState });
      }
    }
  }, [searchParams, smartGroupActive, data, smartGroupOverrides]);

  const updateColumnPrecision = useCallback((field: string, p: DisplayPrecision) => {
    setColumnPrecision((prev) => {
      const next = { ...prev };
      if (p == null) delete next[field];
      else next[field] = p;
      setSearchParams((sp) => {
        const params = new URLSearchParams(sp);
        if (Object.keys(next).length === 0) params.delete("colPrec");
        else params.set("colPrec", btoa(JSON.stringify(next)));
        return params;
      }, { replace: true });
      return next;
    });
  }, [setSearchParams]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getMainMenuItems = useCallback((params: GetMainMenuItemsParams): any[] => {
    const field = params.column?.getColId();
    if (!field || isCategory(field)) return params.defaultItems;

    const current = field in columnPrecision ? columnPrecision[field] : precision;
    const options: { label: string; value: DisplayPrecision }[] = [
      { label: "Auto", value: null },
      ...([1, 2, 3, 4, 5, 6, 8] as const).map((n) => ({ label: `${n}`, value: n as DisplayPrecision })),
    ];

    const precisionMenu: MenuItemDef = {
      name: "Precision",
      subMenu: options.map(({ label, value }) => ({
        name: `${label}${value === current ? "  ✓" : ""}`,
        action: () => updateColumnPrecision(field, value),
      })),
    };

    return [...params.defaultItems, "separator", precisionMenu];
  }, [columnPrecision, precision, updateColumnPrecision]);

  const columnDefs = useMemo<ColDef[]>(() => {
    if (!data?.columns) return [];
    return buildColumnDefs(data.columns, showUncorrected, precision, columnPrecision);
  }, [data?.columns, showUncorrected, precision, columnPrecision]);

  const handleRowClicked = useCallback((event: RowClickedEvent) => {
    const nodeId = event.data?._node_id as string | undefined;
    if (nodeId) selectArtifact(nodeId);
  }, [selectArtifact]);

  const [localSelectedIdx, setLocalSelectedIdx] = useState<number | undefined>();

  const handleCorrelogramPointClick = useCallback((rowIdx: number) => {
    setLocalSelectedIdx(rowIdx);
    const row = correlogramRows[rowIdx];
    const nodeId = row?._node_id as string | undefined;
    if (nodeId) selectArtifact(nodeId);
  }, [correlogramRows, selectArtifact]);

  const correlogramCtxMenu = useDataPointContextMenu();

  const handleCorrelogramContextMenu = useCallback((rowIdx: number, event: React.MouseEvent) => {
    const row = correlogramRows[rowIdx];
    const nodeId = row?._node_id as string | undefined;
    if (nodeId && row) {
      correlogramCtxMenu.setHoveredPoint(nodeId, row);
      correlogramCtxMenu.handleContextMenu(event);
    }
  }, [correlogramRows, correlogramCtxMenu]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getContextMenuItems = useCallback((params: GetContextMenuItemsParams): any[] => {
    const nodeId = params.node?.data?._node_id as string | undefined;
    if (!nodeId) return params.defaultItems ?? [];

    const provenanceItems: MenuItemDef[] = [
      {
        name: "Show Full Lineage",
        action: () => selectArtifactAndOpenPanel(nodeId, "dag"),
      },
      {
        name: "Show Solver Settings",
        action: () => selectArtifactAndOpenPanel(nodeId, "detail"),
      },
      {
        name: "Show Mesh",
        action: () => {
          const meshNodeId = (params.node?.data?._geometry_node_id as string) || nodeId;
          selectArtifactAndOpenPanel(meshNodeId, "dag");
        },
      },
      {
        name: "Show Flowfield",
        action: () => selectArtifactAndOpenPanel(nodeId, "viewer"),
      },
      {
        name: "View Reference Constants",
        action: () => selectArtifactAndOpenPanel(nodeId, "config", { configTab: "reference-quantities" }),
      },
    ];

    return [...(params.defaultItems ?? []), "separator", ...provenanceItems];
  }, [selectArtifactAndOpenPanel]);

  const selectedCorrelogramRowIdx = useMemo(() => {
    if (localSelectedIdx != null) return localSelectedIdx;
    if (!selectedArtifactId) return undefined;
    const idx = correlogramRows.findIndex(r => r._node_id === selectedArtifactId);
    return idx >= 0 ? idx : undefined;
  }, [localSelectedIdx, selectedArtifactId, correlogramRows]);

  const selectedRowRef = useRef<Record<string, unknown> | undefined>(undefined);
  selectedRowRef.current = localSelectedIdx != null ? correlogramRows[localSelectedIdx] : undefined;

  const getRowStyle = useCallback((params: { data?: Record<string, unknown> }): RowStyle | undefined => {
    if (selectedRowRef.current && params.data === selectedRowRef.current) {
      return { background: "var(--accent-a3)" };
    }
    if (params.data?._node_id === selectedArtifactId) {
      return { background: "var(--accent-a3)" };
    }
    return undefined;
  }, [selectedArtifactId]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const target = selectedRowRef.current;
    if (!target && !selectedArtifactId) return;
    api.redrawRows();
    let targetIdx = -1;
    api.forEachNode((node, idx) => {
      if (target && node.data === target) targetIdx = idx;
      else if (!target && node.data?._node_id === selectedArtifactId) targetIdx = idx;
    });
    if (targetIdx >= 0) api.ensureIndexVisible(targetIdx, "middle");
  }, [selectedArtifactId, localSelectedIdx]);

  const cellSelection = useMemo<CellSelectionOptions>(
    () => ({ handle: { mode: "fill" } }),
    [],
  );

  const rowSelection = useMemo<RowSelectionOptions>(
    () => ({ mode: "multiRow", headerCheckbox: true }),
    [],
  );

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    const selected = event.api.getSelectedRows() as Record<string, unknown>[];
    setCheckedNodeIds(
      selected.map((r) => r._node_id as string).filter(Boolean),
    );
  }, []);

  const handleMoveNodes = useCallback((targetBranch: string) => {
    if (checkedNodeIds.length === 0) return;
    moveNodesMutation.mutate(
      { nodeIds: checkedNodeIds, targetBranch },
      {
        onSuccess: () => {
          setCheckedNodeIds([]);
          gridRef.current?.api?.deselectAll();
          setMoveBranchOpen(false);
        },
      },
    );
  }, [checkedNodeIds, moveNodesMutation]);

  const otherBranches = useMemo(
    () => branches?.filter((b) => b.name !== currentBranch) ?? [],
    [branches, currentBranch],
  );

  // -- Stable row data reference to prevent unnecessary AG Grid re-renders --
  const rowData = useMemo(() => data?.rows ?? EMPTY_ROWS, [data?.rows]);
  const rowCount = rowData.length;
  const getRowId = useCallback((params: { data: Record<string, unknown> }) => params.data._node_id as string, []);

  // -- Progressive performance config based on dataset size --
  const perfConfig = useMemo(() => ({
    rowBuffer: rowCount > PERF_TIER_LARGE ? 10 : 20,
    animateRows: false,
    suppressColumnVirtualisation: false,
    debounceVerticalScrollbar: true,
    suppressRowHoverHighlight: rowCount > PERF_TIER_MEDIUM,
  }), [rowCount]);

  if (isLoading) {
    return <div style={{ padding: 16, color: "var(--gray-9)" }}>Loading run table...</div>;
  }

  const tabBtnStyle = (tab: ActiveTab): React.CSSProperties => ({
    ...btnStyle,
    background: activeTab === tab ? "var(--accent-9)" : "var(--gray-a3)",
    color: activeTab === tab ? "white" : "var(--gray-12)",
  });


  return (
    <div style={{ height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 12px", borderBottom: "1px solid var(--gray-a5)" }}>
        <button data-tour="tab-table" onClick={() => setActiveTab("table")} style={tabBtnStyle("table")}>Table</button>
        <button data-tour="tab-correlogram" onClick={() => setActiveTab("correlogram")} style={tabBtnStyle("correlogram")}>Correlogram</button>
        <button
          onClick={smartGroupActive ? clearGroups : () => applySmartGroup()}
          style={{
            ...btnStyle,
            background: smartGroupActive ? "var(--blue-9)" : "var(--gray-a3)",
            color: smartGroupActive ? "white" : "var(--gray-12)",
          }}
        >
          {smartGroupActive ? "Clear Groups" : "Smart Group"}
        </button>
        <button
          onClick={toggleUncorrected}
          title="Show raw Flow360 values alongside corrected data"
          style={{
            ...btnStyle,
            background: showUncorrected ? "var(--amber-9)" : "var(--gray-a3)",
            color: showUncorrected ? "white" : "var(--gray-12)",
          }}
        >
          {showUncorrected ? "Showing Uncorrected" : "Show Uncorrected"}
        </button>
        <select
          value={precision ?? "auto"}
          onChange={(e) => {
            const v = e.target.value;
            const next: DisplayPrecision = v === "auto" ? null : Number(v);
            setPrecision(next);
            setSearchParams((sp) => {
              const p = new URLSearchParams(sp);
              if (next == null) p.delete("precision");
              else p.set("precision", String(next));
              return p;
            }, { replace: true });
          }}
          style={{
            ...btnStyle,
            background: "var(--gray-a3)",
            color: "var(--gray-12)",
            appearance: "auto",
          }}
          title="Default decimal places (right-click a column header to override per-column)"
        >
          <option value="auto">Auto precision</option>
          {[1, 2, 3, 4, 5, 6, 8].map((n) => (
            <option key={n} value={n}>{n} decimals</option>
          ))}
        </select>
        {hasInFlight && (
          <span style={{ fontSize: 11, color: "var(--amber-11)" }}>
            {data?.rows?.filter((r) => IN_FLIGHT_STATUSES.includes(r.status as string)).length} runs in-flight
          </span>
        )}
        {hasMore && isLoadingRest && (
          <span style={{ fontSize: 11, color: "var(--gray-9)", marginLeft: "auto" }}>
            Loading {restLimit} more rows...
          </span>
        )}
        {totalRows > FULL_LOAD_THRESHOLD && !isLoadingRest && (
          <span style={{ fontSize: 11, color: "var(--amber-11)", marginLeft: hasMore && isLoadingRest ? 0 : "auto" }}>
            Showing first {FULL_LOAD_THRESHOLD.toLocaleString()} of {totalRows.toLocaleString()} cases — use filters to narrow down
          </span>
        )}
      </div>

      {smartGroupActive && smartGroupExplanation && (
        <SmartGroupPanel
          explanation={smartGroupExplanation}
          overrides={smartGroupOverrides}
          onOverridesChange={handleSmartGroupOverridesChange}
          allFields={allFieldNames}
          groups={correlogramGroups}
          collapsed={smartGroupPanelCollapsed}
          onToggleCollapsed={() => setSmartGroupPanelCollapsed((v) => !v)}
        />
      )}

      {activeTab === "correlogram" && (
        <>
          <ColumnSelector
            columns={nonConstantNumericDefs}
            selectedKeys={selectedColumnKeys}
            onSelectionChange={setSelectedColumnKeys}
            rows={correlogramRows}
            colorByKey={colorByKey}
            onColorByChange={setColorByKey}
            colorByOptions={allColumnOptions}
            groupByKey={groupByKey}
            onGroupByChange={(k) => {
              setGroupByKey(k);
              setSearchParams((sp) => { const p = new URLSearchParams(sp); if (k) p.set("groupBy", k); else p.delete("groupBy"); return p; }, { replace: true });
            }}
            sizeByKey={sizeByKey}
            onSizeByChange={setSizeByKey}
            symbolByKey={symbolByKey}
            onSymbolByChange={setSymbolByKey}
            zAxisKey={zAxisKey}
            onZAxisChange={setZAxisKey}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 12px", fontSize: 11, borderBottom: "1px solid var(--gray-a4)" }}>
            <span style={{ color: "var(--gray-9)" }}>Data:</span>
            {(["all", "filtered", "aggregated"] as const).map((src) => (
              <button
                key={src}
                onClick={() => setDataSource(src)}
                disabled={src === "aggregated" && !smartGroupActive}
                style={{
                  ...btnStyle, fontSize: 11, padding: "1px 6px",
                  background: dataSource === src ? "var(--accent-9)" : "var(--gray-a3)",
                  color: dataSource === src ? "white" : "var(--gray-12)",
                  opacity: src === "aggregated" && !smartGroupActive ? 0.4 : 1,
                }}
              >
                {src === "all" ? "All" : src === "filtered" ? "Filtered" : "Aggregated"}
              </button>
            ))}
            <div style={{ width: 1, height: 14, background: "var(--gray-a5)", margin: "0 2px" }} />
            <button
              onClick={() => setExcludeOutliers((v) => !v)}
              style={{
                ...btnStyle, fontSize: 11, padding: "1px 6px",
                background: excludeOutliers ? "var(--red-9)" : "var(--gray-a3)",
                color: excludeOutliers ? "white" : "var(--gray-12)",
              }}
            >
              {excludeOutliers ? "Outliers Excluded" : "Exclude Outliers"}
            </button>
            <span style={{ color: "var(--gray-8)", marginLeft: "auto" }}>
              {effectiveRows.length} pts
            </span>
          </div>
        </>
      )}

      {checkedNodeIds.length > 0 && activeTab === "table" && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "6px 12px",
          background: "var(--accent-a3)", borderBottom: "1px solid var(--accent-a6)",
        }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: "var(--accent-11)" }}>
            {checkedNodeIds.length} run{checkedNodeIds.length !== 1 ? "s" : ""} selected
          </span>
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setMoveBranchOpen((v) => !v)}
              disabled={otherBranches.length === 0 || moveNodesMutation.isPending}
              style={{
                ...btnStyle,
                background: "var(--accent-9)", color: "white",
                opacity: otherBranches.length === 0 ? 0.5 : 1,
              }}
            >
              {moveNodesMutation.isPending ? "Moving..." : "Move to branch..."}
            </button>
            {moveBranchOpen && otherBranches.length > 0 && (
              <div style={{
                position: "absolute", top: "100%", left: 0, zIndex: 50,
                marginTop: 4, minWidth: 180,
                background: "var(--color-surface)", border: "1px solid var(--gray-a5)",
                borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,.15)",
                overflow: "hidden",
              }}>
                {otherBranches.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => handleMoveNodes(b.name)}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: "6px 12px", border: "none",
                      background: "transparent", cursor: "pointer",
                      fontSize: 12, color: "var(--gray-12)",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--gray-a3)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    {b.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => { setCheckedNodeIds([]); gridRef.current?.api?.deselectAll(); }}
            style={{ ...btnStyle, marginLeft: "auto" }}
          >
            Clear
          </button>
        </div>
      )}

      <div
        data-tour="run-table"
        style={{ flex: 1, width: "100%", display: activeTab === "table" ? "block" : "none" }}
      >
        <AgGridReact
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columnDefs}
          rowData={rowData}
          getRowId={getRowId}
          rowSelection={rowSelection}
          onSelectionChanged={handleSelectionChanged}
          enableAdvancedFilter
          cellSelection={cellSelection}
          sideBar
          rowGroupPanelShow="always"
          aggFuncs={ALL_CUSTOM_AGGS}
          getMainMenuItems={getMainMenuItems}
          getContextMenuItems={getContextMenuItems}
          onGridReady={handleGridReady}
          onFilterChanged={handleFilterChanged}
          onRowClicked={handleRowClicked}
          getRowStyle={getRowStyle}
          rowBuffer={perfConfig.rowBuffer}
          animateRows={perfConfig.animateRows}
          suppressColumnVirtualisation={perfConfig.suppressColumnVirtualisation}
          debounceVerticalScrollbar={perfConfig.debounceVerticalScrollbar}
          suppressRowHoverHighlight={perfConfig.suppressRowHoverHighlight}
          statusBar={{
            statusPanels: [
              { statusPanel: "agTotalAndFilteredRowCountComponent" },
              { statusPanel: "agAggregationComponent" },
            ],
          }}
        />
      </div>

      {activeTab === "correlogram" && (
        <div data-tour="correlogram" style={{ flex: 1, width: "100%", minHeight: 0 }}>
          <Correlogram
            rows={correlogramRows}
            numericColumns={activeCorrelogramColumns}
            groups={correlogramGroups}
            colorByKey={groupByKey ? undefined : colorByKey}
            sizeByKey={sizeByKey}
            symbolByKey={symbolByKey}
            zAxisKey={zAxisKey}
            onPointClick={handleCorrelogramPointClick}
            onPointContextMenu={handleCorrelogramContextMenu}
            selectedRowIdx={selectedCorrelogramRowIdx}
          />
          {correlogramCtxMenu.menu && <DataPointContextMenu menu={correlogramCtxMenu.menu} onClose={correlogramCtxMenu.close} />}
        </div>
      )}
    </div>
  );
}
